import {useCallback, useEffect, useState} from "react";
import {App, Button, Card, Input, Popconfirm, Space, Table, Tag, Typography} from "antd";
import {PlusOutlined, ReloadOutlined} from "@ant-design/icons";

import {useAuth} from "@modules/auth/AuthContext.jsx";
import * as usersApi from "@modules/users/usersApi.js";
import UserFormModal from "@modules/users/components/UserFormModal.jsx";

const ROLE_TAG = {superadmin: "red", admin: "blue", operator: "default"};
const EMPTY_MODAL = {open: false, mode: "create", record: null};

function apiError(e)
{
	const code = e?.response?.data?.error;
	const map = {
		cannot_self_deactivate: "Non puoi disattivare te stesso",
		last_superadmin: "Non puoi disattivare l'ultimo superadmin",
		items_required_on_activate: "Riattivazione: assegna voci + landing"
	};
	return map[code] || "Operazione non riuscita";
}

export default function UsersPage()
{
	const {role, allowedMenuItems} = useAuth();
	const {message} = App.useApp();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [q, setQ] = useState("");
	const [catalog, setCatalog] = useState({assignable: [], presets: {}});
	const [modal, setModal] = useState(EMPTY_MODAL);

	const load = useCallback(async () =>
	{
		setLoading(true);
		try { setRows(await usersApi.listUsers(q)); }
		catch { message.error("Errore nel caricamento utenti"); }
		finally { setLoading(false); }
	}, [q, message]);

	useEffect(() => { load(); }, [load]);
	useEffect(() => { usersApi.getCatalog().then(setCatalog).catch(() => {}); }, []);

	const onDeactivate = async (rec) =>
	{
		try { await usersApi.deactivateUser(rec.id || rec._id); message.success("Utente disattivato"); load(); }
		catch (e) { message.error(apiError(e)); }
	};

	const onActivate = async (rec) =>
	{
		// Riattivazione di un non-superadmin svuotato → apri il form per assegnare voci+landing.
		if (rec.role !== "superadmin" && (!rec.allowedMenuItems || rec.allowedMenuItems.length === 0))
		{
			setModal({open: true, mode: "reactivate", record: rec});
			return;
		}
		try { await usersApi.activateUser(rec.id || rec._id); message.success("Utente riattivato"); load(); }
		catch (e) { message.error(apiError(e)); }
	};

	const columns = [
		{title: "Username", dataIndex: "username"},
		{title: "Nome", dataIndex: "displayName"},
		{title: "Ruolo", dataIndex: "role", render: (r) => <Tag color={ROLE_TAG[r]}>{r}</Tag>},
		{
			title: "Voci",
			dataIndex: "allowedMenuItems",
			render: (it, rec) => rec.role === "superadmin" ? <Tag>tutte</Tag> : (it || []).length
		},
		{title: "Landing", dataIndex: "landingMenuItem"},
		{
			title: "Stato",
			dataIndex: "active",
			render: (a) => a ? <Tag color="green">attivo</Tag> : <Tag color="red">disattivato</Tag>
		},
		{
			title: "Azioni",
			key: "actions",
			render: (_, rec) => (
				<Space>
					<Button size="small" onClick={() => setModal({open: true, mode: "edit", record: rec})}>Modifica</Button>
					{rec.active
						? (
							<Popconfirm title="Disattivare l'utente?" okText="Sì" cancelText="No" onConfirm={() => onDeactivate(rec)}>
								<Button size="small" danger>Disattiva</Button>
							</Popconfirm>
						)
						: <Button size="small" onClick={() => onActivate(rec)}>Riattiva</Button>}
				</Space>
			)
		}
	];

	return (
		<Card className="card-elev">
			<Space style={{marginBottom: 12, justifyContent: "space-between", width: "100%"}} wrap>
				<Typography.Title level={4} style={{margin: 0}}>Gestione Utenti</Typography.Title>
				<Space wrap>
					<Input.Search placeholder="Cerca username/nome…" allowClear onSearch={setQ} style={{width: 240}}/>
					<Button icon={<ReloadOutlined/>} onClick={load}/>
					<Button type="primary" icon={<PlusOutlined/>} onClick={() => setModal({open: true, mode: "create", record: null})}>
						Nuovo utente
					</Button>
				</Space>
			</Space>

			<Table
				rowKey={(r) => r.id || r._id}
				columns={columns}
				dataSource={rows}
				loading={loading}
				size="small"
				pagination={{pageSize: 20, hideOnSinglePage: true}}
			/>

			<UserFormModal
				open={modal.open}
				mode={modal.mode}
				record={modal.record}
				actor={{role, allowedMenuItems}}
				catalog={catalog}
				onClose={() => setModal(EMPTY_MODAL)}
				onSaved={() => { setModal(EMPTY_MODAL); load(); }}
			/>
		</Card>
	);
}
